import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://incendios.justclean.es';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': `${siteUrl}/servicios#service`,
    serviceType: 'Limpieza Post Incendio',
    provider: {
      '@type': 'LocalBusiness',
      '@id': `${siteUrl}/#organization`,
      name: 'JUSTCLEAN INCENDIOS',
      telephone: '+34602365996',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Barcelona',
        addressCountry: 'ES',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Barcelona',
    },
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Servicios de Limpieza Post Incendio',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Eliminación de Hollín y Humo',
            description: 'Limpieza técnica de paredes, techos y suelos con productos especializados',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Neutralización de Olores',
            description: 'Tratamientos profesionales para eliminar el olor a humo persistente',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Retirada de Residuos',
            description: 'Desescombro y retirada de enseres dañados',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Limpieza Profunda de Cocinas',
            description: 'Desengrase y limpieza de campanas, azulejos y superficies',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Desinfección y Saneamiento',
            description: 'Protocolos de higienización completa con ventilación controlada',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Preparación para Pintura',
            description: 'Acondicionamiento de superficies para trabajos de reparación',
          },
        },
      ],
    },
  };

  const services = [
    {
      icon: 'ri-brush-line',
      title: 'Eliminación de Hollín y Humo de Paredes y Techos',
      description: 'El hollín es altamente corrosivo y se adhiere a todas las superficies. Utilizamos técnicas de aspiración industrial con filtros HEPA para capturar las partículas finas, seguido de lavado técnico con productos especializados según el tipo de material (pintura, yeso, madera, azulejo, etc.). El objetivo es eliminar completamente las manchas negras y las partículas ácidas que continúan dañando las superficies.',
      details: [
        'Aspiración industrial de partículas de hollín',
        'Lavado técnico con productos específicos por material',
        'Tratamiento de manchas persistentes',
        'Preparación de superficies para pintura posterior',
      ],
    },
    {
      icon: 'ri-wind-line',
      title: 'Neutralización de Olores a Humo',
      description: 'El olor a humo es uno de los problemas más persistentes tras un incendio. Las partículas de humo se infiltran en tejidos, muebles, sistemas de ventilación y materiales porosos. Aplicamos tratamientos profesionales de desodorización que neutralizan las moléculas causantes del mal olor, no solo las enmascaran. Dependiendo de la gravedad, utilizamos generadores de ozono, nebulizadores con productos enzimáticos o selladores específicos.',
      details: [
        'Evaluación del nivel de penetración del olor',
        'Tratamientos de desodorización profesional',
        'Generadores de ozono (cuando es apropiado)',
        'Ventilación controlada y sellado de superficies',
      ],
    },
    {
      icon: 'ri-delete-bin-line',
      title: 'Retirada de Residuos y Enseres Dañados',
      description: 'Tras un incendio, es común que haya enseres, muebles, electrodomésticos y materiales estructurales que no puedan recuperarse. Ofrecemos el servicio de desescombro y retirada de residuos, coordinando con gestores autorizados para el tratamiento adecuado de materiales peligrosos (amianto, productos químicos, etc.). Este servicio se realiza bajo petición y se coordina con el perito para documentar correctamente los bienes irrecuperables.',
      details: [
        'Clasificación de enseres recuperables vs. irrecuperables',
        'Desescombro y retirada de materiales dañados',
        'Gestión de residuos según normativa',
        'Coordinación con perito para documentación',
      ],
    },
    {
      icon: 'ri-restaurant-line',
      title: 'Limpieza Profunda de Cocinas Quemadas',
      description: 'Los incendios en cocinas dejan residuos grasos mezclados con hollín que son especialmente difíciles de eliminar. Campanas extractoras, azulejos, encimeras y electrodomésticos quedan cubiertos de una capa pegajosa y corrosiva. Realizamos desengrase profundo con productos industriales específicos, limpieza de conductos de extracción (si es necesario) y saneamiento completo de todas las superficies para devolver la funcionalidad a la cocina.',
      details: [
        'Desengrase de campanas y extractores',
        'Limpieza de azulejos y superficies cerámicas',
        'Tratamiento de residuos grasos de hollín',
        'Saneamiento de electrodomésticos recuperables',
      ],
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Desinfección y Saneamiento Post Incendio',
      description: 'Además del hollín y el humo, un incendio puede generar condiciones propicias para el crecimiento de moho (por el agua de extinción) y contaminación bacteriana. Realizamos protocolos de desinfección y saneamiento con productos homologados, ventilación controlada y tratamiento de humedades residuales. El objetivo es garantizar que el espacio sea seguro y habitable antes de proceder con las reparaciones.',
      details: [
        'Desinfección con productos homologados',
        'Control de humedades por agua de extinción',
        'Prevención de moho y contaminación',
        'Ventilación controlada del espacio',
      ],
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Preparación para Pintura y Reparaciones',
      description: 'Una vez eliminado el hollín, neutralizados los olores y saneadas las superficies, preparamos el espacio para que los profesionales de pintura y reparación puedan trabajar sin problemas. Esto incluye el sellado de superficies porosas, aplicación de imprimaciones anti-manchas (si es necesario) y limpieza final. Entregamos el espacio listo para la siguiente fase de restauración.',
      details: [
        'Sellado de superficies porosas',
        'Aplicación de imprimaciones anti-manchas',
        'Limpieza final y verificación de calidad',
        'Coordinación con equipos de pintura/reparación',
      ],
    },
  ];

  const avoidTips = [
    {
      icon: 'ri-close-circle-line',
      title: 'No limpies con productos domésticos',
      description: 'El hollín es ácido y corrosivo. Los productos de limpieza domésticos pueden extender las manchas o dañar más las superficies.',
    },
    {
      icon: 'ri-close-circle-line',
      title: 'No toques paredes con las manos',
      description: 'El contacto directo puede fijar el hollín en la superficie y hacer más difícil su eliminación posterior.',
    },
    {
      icon: 'ri-close-circle-line',
      title: 'No uses agua directamente',
      description: 'El agua puede convertir el hollín seco en una pasta que penetra más profundamente en materiales porosos.',
    },
    {
      icon: 'ri-close-circle-line',
      title: 'No enciendas la calefacción o aire acondicionado',
      description: 'Puede dispersar partículas de hollín por todo el sistema de ventilación y contaminar otras áreas.',
    },
    {
      icon: 'ri-close-circle-line',
      title: 'No intentes lavar textiles en casa',
      description: 'Cortinas, ropa y tejidos afectados requieren limpieza especializada. El lavado doméstico puede fijar el olor permanentemente.',
    },
    {
      icon: 'ri-close-circle-line',
      title: 'No demores la intervención profesional',
      description: 'Cada día que pasa, el hollín se adhiere más y el olor se intensifica. Actuar rápido minimiza los daños permanentes.',
    },
  ];

  return (
    <>
      <SEOHead
        title="Servicios Limpieza Post Incendio Barcelona | JUSTCLEAN"
        description="Eliminación de hollín, neutralización de olores, retirada de residuos, desinfección. Servicios completos de limpieza tras incendio en Barcelona. ☎ 602 36 59 96"
        keywords="servicios limpieza post incendio, eliminación hollín Barcelona, neutralizar olor humo, desescombro incendio, limpieza cocina quemada"
        canonical="/servicios"
        structuredData={structuredData}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Servicios de limpieza post incendio
            </h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Soluciones integrales para la recuperación de espacios afectados por incendios. Desde la eliminación de hollín hasta la preparación para pintura.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Detail */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="bg-gray-50 rounded-lg p-8"
              >
                <div className="flex items-start gap-6">
                  <div className="w-14 h-14 bg-red-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className={`${service.icon} text-2xl text-[#ff0000]`}></i>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      {service.title}
                    </h2>
                    <p className="text-base text-gray-700 mb-6">
                      {service.description}
                    </p>
                    <div className="grid md:grid-cols-2 gap-3">
                      {service.details.map((detail, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <i className="ri-checkbox-circle-fill text-[#ff0000] text-lg mt-0.5"></i>
                          <span className="text-sm text-gray-700">{detail}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What NOT to Do */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Qué NO Hacer Después de un Incendio
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Evita estos errores comunes que pueden empeorar los daños o comprometer la reclamación al seguro.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {avoidTips.map((tip, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#ff0000]"
              >
                <div className="flex items-start gap-4">
                  <i className={`${tip.icon} text-2xl text-[#ff0000] mt-1`}></i>
                  <div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2">
                      {tip.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {tip.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 bg-red-50 border-l-4 border-[#ff0000] p-6 max-w-4xl mx-auto">
            <p className="text-sm text-gray-800">
              <strong>Recomendación importante:</strong> Antes de iniciar cualquier limpieza profunda, contacta con tu aseguradora y espera la visita del perito. Documenta todo con fotografías y no tires nada sin autorización. Una intervención prematura puede comprometer tu reclamación. Si tienes dudas, consulta nuestras <Link to="/faq" className="text-[#ff0000] hover:underline font-semibold">preguntas frecuentes sobre seguros y limpieza post incendio</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Proceso de Trabajo Completo
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Desde el primer contacto hasta la entrega final, seguimos un protocolo estructurado. Nuestro <Link to="/empresa" className="text-[#ff0000] hover:underline font-semibold">equipo profesional especializado</Link> te acompaña en cada fase.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                {
                  step: '1',
                  title: 'Contacto y Evaluación Inicial',
                  description: 'Recibimos tu llamada o mensaje, recopilamos información básica (ubicación, tipo de incendio, extensión) y coordinamos una visita de evaluación. Si ya tienes perito asignado, coordinamos con él.',
                },
                {
                  step: '2',
                  title: 'Inspección y Presupuesto',
                  description: 'Visitamos el lugar, documentamos fotográficamente los daños, identificamos materiales afectados y elaboramos un presupuesto detallado. Te explicamos qué trabajos son necesarios y los tiempos estimados.',
                },
                {
                  step: '3',
                  title: 'Coordinación con Seguro',
                  description: 'Si procede, enviamos el presupuesto al perito de la aseguradora y esperamos su aprobación. Te asesoramos sobre la documentación necesaria para la reclamación.',
                },
                {
                  step: '4',
                  title: 'Ejecución de la Limpieza',
                  description: 'Iniciamos los trabajos con EPIs, protegemos zonas comunes, aplicamos técnicas especializadas de limpieza de hollín, desodorización y saneamiento. Mantenemos comunicación constante sobre el progreso.',
                },
                {
                  step: '5',
                  title: 'Verificación y Entrega',
                  description: 'Realizamos control de calidad, verificamos que el olor esté neutralizado y las superficies limpias. Entregamos el espacio listo para pintura/reparación con informe fotográfico final.',
                },
              ].map((item, index) => (
                <div key={index} className="flex gap-6">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[#ff0000] text-white rounded-full flex items-center justify-center font-bold text-lg">
                      {item.step}
                    </div>
                  </div>
                  <div className="flex-1 pb-8 border-l-2 border-gray-200 pl-6 -ml-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 ml-6">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-600 ml-6">
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">
            ¿Necesitas Alguno de Estos Servicios?
          </h2>
          <p className="text-lg text-gray-300 mb-8">
            Contacta con nosotros para una evaluación personalizada y presupuesto sin compromiso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+34602365996"
              className="px-8 py-4 bg-[#ff0000] text-white text-base font-semibold rounded-lg hover:bg-[#cc0000] transition-all whitespace-nowrap"
            >
              <i className="ri-phone-line mr-2"></i>
              602 36 59 96
            </a>
            <Link
              to="/contacto"
              className="px-8 py-4 bg-white text-gray-900 text-base font-semibold rounded-lg hover:bg-gray-100 transition-all whitespace-nowrap"
            >
              <i className="ri-mail-line mr-2"></i>
              Formulario de Contacto
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ServicesPage;
